/**
 * Orders module — contracts from api-servicios-ordenes-logs.md §3.2–3.3.
 * List/detail UI use the reduced list-orders payload only (no DFT sync object).
 */

import dayjs, { type Dayjs } from "dayjs";
import { orderBackendUrl } from "../config/gateway";

/** Process result on POST /order/reprocess item results */
export type OrderProcessResult =
  | "SUCCESS"
  | "FAILED"
  | "IN_PROGRESS"
  | "CANCELED"
  | "UNKNOWN";

/**
 * Reduced list-orders row (§3.2).
 * ASSUMPTION: `total` is displayed as-is (same as PricesPage). Doc says
 * "centavos VTEX" but sample magnitudes match COP enteros; filter sends
 * the exact API number.
 * ASSUMPTION: `creationDate` is not in the current §3.2 field table; map
 * it when present for older payloads / mocks, else null.
 */
export interface OrderListItem {
  orderVTEXId: string;
  /** SIESA / O.C. id (`sale.order_erp`); null if not created yet */
  orderSIESAId: string | null;
  documentId: string;
  names: string;
  /** Optional; not listed in §3.2 content[] — null when API omits it */
  creationDate: string | null;
  trackingNumber: string | null;
  status: string;
  total: number;
  processedAt: string | null;
  errorDetail: string | null;
  /** Automatic retries (`sale.retry`) */
  retryCount: number | null;
  /** Manual retries (reprocess from dashboard) */
  attemptCount: number | null;
}

/** Drawer source = list row (no GET /order/{id}). */
export type OrderDetail = OrderListItem;

/**
 * Filters mapped to GET /order/list-orders query params (§3.2).
 * Exact match on orderVTEXId / orderSIESAId / total / status.
 * Dates must fall inside the backend 2-month window or API returns 400.
 */
export interface OrderListFilters {
  search: string;
  filterType: OrderFilterType;
  /** `sale.status_sale`; "ALL" omits the `status` query param */
  status: string;
  dateFrom: string | null;
  dateTo: string | null;
}

/** Search fields supported as list-orders query params */
export type OrderFilterType = "ORDER_VTEX_ID" | "ORDER_SIESA_ID" | "TOTAL";

export interface OrderListResponse {
  content: OrderListItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

/** Per-order result from POST /order/reprocess (DFT + API md supersets) */
export interface OrderReprocessItemResult {
  orderId: string;
  success: boolean;
  skipped: boolean;
  message: string;
  statusBefore: string | null;
  statusAfter: string | null;
  processResult: OrderProcessResult | null;
  errorDetail: string | null;
}

export interface OrderReprocessProgress {
  /** Client-side batch id (not a server job) */
  jobId: string;
  total: number;
  processed: number;
  pending: number;
  successCount: number;
  noveltyCount: number;
  percent: number;
  currentOrderVTEXId: string | null;
  status: "RUNNING" | "COMPLETED" | "FAILED";
  message: string;
  results: OrderReprocessItemResult[];
}

/**
 * Integration statuses from `sale.status_sale` (list-orders `status`).
 * UI mapping follows CatalogStatusTag pattern: Spanish label + tone.
 */
export type OrderIntegrationStatus =
  | "synchronized"
  | "pending-create-client"
  | "created-client"
  | "pending-generate-guide"
  | "generated-guide"
  | "pending-create-order"
  | "created-order"
  | "handling"
  | "pending-invoiced"
  | "delivered"
  | "invoiced"
  | "payment-rejected"
  | "canceled"
  | "cancelled";

export type OrderStatusTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "blue"
  | "canceled"
  | "default";

/** Labels derived from sale.status_sale (service values → short Spanish). */
export const ORDER_STATUS_LABELS: Record<OrderIntegrationStatus, string> = {
  synchronized: "Procesando",
  "pending-create-client": "Pendiente cliente",
  "created-client": "Cliente creado",
  "pending-generate-guide": "Pendiente guía",
  "generated-guide": "Guía generada",
  "pending-create-order": "Pendiente pedido",
  "created-order": "Creado",
  handling: "En trámite",
  "pending-invoiced": "Pendiente facturación",
  delivered: "Enviado",
  invoiced: "Facturado",
  "payment-rejected": "Pago Rechazado",
  canceled: "Cancelado",
  cancelled: "Cancelado",
};

/** Tone classes aligned with CatalogStatusTag (success/warning/error/info). */
export const ORDER_STATUS_TONES: Record<OrderIntegrationStatus, OrderStatusTone> =
  {
    // "En curso" per §4.4 (sync VTEX, aún sin procesar) — not a failure.
    synchronized: "warning",
    "pending-create-client": "error",
    "created-client": "info",
    "pending-generate-guide": "error",
    "generated-guide": "info",
    "pending-create-order": "error",
    "created-order": "success",
    handling: "info",
    "pending-invoiced": "error",
    delivered: "info",
    invoiced: "blue",
    "payment-rejected": "error",
    canceled: "canceled",
    cancelled: "canceled",
  };

/**
 * Intermediate/post-SIESA states shown to ops as another state (label + tone).
 * The raw `status` is kept on the row, so reprocess eligibility is unaffected.
 */
export const ORDER_STATUS_DISPLAY_ALIASES: Partial<
  Record<OrderIntegrationStatus, OrderIntegrationStatus>
> = {
  "created-client": "pending-generate-guide",
  "generated-guide": "pending-create-order",
  // Session 2026-09-18: everything after "Creado" (VTEX-side steps) shows as Creado.
  handling: "created-order",
  "pending-invoiced": "created-order",
  invoiced: "created-order",
  delivered: "created-order",
};

const resolveDisplayStatus = (status: string): OrderIntegrationStatus => {
  const key = status as OrderIntegrationStatus;
  return ORDER_STATUS_DISPLAY_ALIASES[key] ?? key;
};

export const getOrderStatusLabel = (status: string): string =>
  ORDER_STATUS_LABELS[resolveDisplayStatus(status)] ?? status;

export const getOrderStatusTone = (status: string): OrderStatusTone =>
  ORDER_STATUS_TONES[resolveDisplayStatus(status)] ?? "default";

/**
 * Masked filters are grouped server-side (confirmed by backend, 2026-09-23):
 * e.g. `status=created-order` returns created-order + handling +
 * pending-invoiced + invoiced + delivered, and `status=pending-generate-guide`
 * also returns created-client. The front sends the displayed status as-is.
 */

/** Compares by displayed state, so backend-grouped rows (invoiced under created-order) are kept. */
const normalizeStatusForMatch = (status: string | null | undefined): string => {
  const value = (status ?? "").toLowerCase();
  return resolveDisplayStatus(value === "cancelled" ? "canceled" : value);
};

/**
 * Drops rows whose displayed state differs from the queried one. list-orders
 * has returned rows in another state under a status filter (e.g. an order
 * moving synchronized → pending-create-order mid-request), which mixed
 * states in the table and broke count vs rows.
 */
export const filterOrdersByStatus = (
  orders: OrderListItem[],
  status: string
): OrderListItem[] => {
  if (!status || status === "ALL") return orders;
  const expected = normalizeStatusForMatch(status);
  return orders.filter(
    (order) => normalizeStatusForMatch(order.status) === expected
  );
};

const HIDDEN_FILTER_STATUSES = new Set<string>([
  // Keep a single "Cancelado" entry (API may send canceled or cancelled)
  "cancelled",
  "payment-rejected",
  ...Object.keys(ORDER_STATUS_DISPLAY_ALIASES),
]);

/** Fixed filter options (not derived from current page rows). */
export const ORDER_STATUS_FILTER_OPTIONS: {
  value: string;
  label: string;
}[] = [
  { value: "ALL", label: "Todos (estado)" },
  ...(
    Object.entries(ORDER_STATUS_LABELS) as [OrderIntegrationStatus, string][]
  )
    .filter(([status]) => !HIDDEN_FILTER_STATUSES.has(status))
    .map(([status, label]) => ({ value: status, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "es")),
];

/**
 * States eligible for POST /order/reprocess (§3.3).
 * `synchronized` ("Procesando") is reprocessable per backend contract; the
 * server validates VTEX ready-for-handling before running initProcess and
 * returns a per-order error otherwise (see OrderReprocessProgress result).
 */
export const REPROCESSABLE_STATUSES = new Set([
  "synchronized",
  "pending-create-client",
  "pending-generate-guide",
  "pending-create-order",
  "created-client",
  "generated-guide",
]);

export const NON_REPROCESSABLE_STATUSES = new Set([
  "created-order",
  "canceled",
  "cancelled",
  "processed",
  "handling",
  "pending-invoiced",
  "partial-invoiced",
  "invoiced",
  "delivered",
  "payment-rejected",
]);

export const ORDER_REPROCESS_MAX_BATCH = 20;

/** Base timeout per order in a batch (backend runs initProcess sequentially) */
export const ORDER_REPROCESS_TIMEOUT_MS = 90_000;

/**
 * Gateway `order-reprocess` TimeLimiter is 7 minutes (§3.3).
 * Cap client AbortController to match so we do not abort before the gateway.
 */
export const ORDER_REPROCESS_MAX_TIMEOUT_MS = 420_000;

/** Backend list-orders always scopes `create_at` to the last N months (§3.2). */
export const ORDER_LIST_WINDOW_MONTHS = 2;

/**
 * [today − N months, today] as local calendar days. Shared by the date picker
 * guard and the default query range so both use the same bounds.
 * ASSUMPTION: users are in America/Bogota, the zone the backend validates in.
 */
export const getOrderListDateWindow = (): { min: Dayjs; max: Dayjs } => {
  const max = dayjs().startOf("day");
  return { min: max.subtract(ORDER_LIST_WINDOW_MONTHS, "month"), max };
};

export const ORDER_LIST_DATE_WINDOW_MESSAGE =
  "Las fechas deben estar dentro de los últimos 2 meses. Ajusta el rango e inténtalo de nuevo.";

export const ORDER_FILTER_TYPE_OPTIONS: {
  value: OrderFilterType;
  label: string;
}[] = [
  { value: "ORDER_VTEX_ID", label: "Order VTEX" },
  { value: "ORDER_SIESA_ID", label: "Order SIESA" },
  { value: "TOTAL", label: "Total" },
];

export const ORDER_FILTER_TYPE_PLACEHOLDERS: Record<OrderFilterType, string> = {
  ORDER_VTEX_ID: "ID Order VTEX (exacto)…",
  ORDER_SIESA_ID: "ID Order SIESA (exacto)…",
  TOTAL: "Total exacto (valor API)…",
};

/**
 * Parses search text into list-orders `total` (exact numeric match).
 * Digits only so "692.000" / "$692000" → 692000.
 */
export const parseOrderTotalFilter = (raw: string): number | null => {
  const digits = raw.trim().replace(/\D/g, "");
  if (!digits) return null;
  const value = Number(digits);
  if (!Number.isFinite(value)) return null;
  return Math.trunc(value);
};

/**
 * Appends §3.2 filter query params (not page/size) onto URLSearchParams.
 */
export const appendOrderListFilterParams = (
  params: URLSearchParams,
  filters: OrderListFilters
): void => {
  // Always send the 2-month window explicitly instead of relying on the
  // backend default, so every list/count query is scoped the same way.
  const range = getOrderListDateWindow();
  params.set("fromDate", filters.dateFrom ?? range.min.format("YYYY-MM-DD"));
  params.set("toDate", filters.dateTo ?? range.max.format("YYYY-MM-DD"));

  const term = filters.search.trim();
  if (term) {
    if (filters.filterType === "ORDER_VTEX_ID") {
      params.set("orderVTEXId", term);
    } else if (filters.filterType === "ORDER_SIESA_ID") {
      params.set("orderSIESAId", term);
    } else if (filters.filterType === "TOTAL") {
      const total = parseOrderTotalFilter(term);
      if (total !== null) params.set("total", String(total));
    }
  }

  if (filters.status && filters.status !== "ALL") {
    params.set("status", filters.status);
  }
};

export const ORDER_LIST_URL = orderBackendUrl.listOrders();

export const ORDER_REPROCESS_URL = orderBackendUrl.reprocess();

export const ORDER_LOAD_ERROR_MESSAGE =
  "No se pudieron cargar las órdenes. Inténtalo de nuevo.";

export const ORDER_REPROCESS_SUCCESS_MESSAGE =
  "El reproceso a SIESA finalizó. Revisa el resultado de cada orden.";

export const ORDER_REPROCESS_ERROR_MESSAGE =
  "No se pudo completar el reproceso a SIESA. Inténtalo de nuevo.";

export const ORDER_REPROCESS_UNAVAILABLE_MESSAGE =
  "El endpoint de reproceso no está disponible aún. Contacta a backend.";

/** Gateway/service rejected POST on /order/reprocess (HTTP 405). */
export const ORDER_REPROCESS_METHOD_NOT_ALLOWED_MESSAGE =
  "El servicio rechazó el reproceso (HTTP 405). POST /order-backend/order/reprocess no está habilitado en el gateway o en order-backend. Contacta a backend para publicar esa ruta.";

export const EMPTY_ORDER_FILTERS: OrderListFilters = {
  search: "",
  filterType: "ORDER_VTEX_ID",
  status: "ALL",
  dateFrom: null,
  dateTo: null,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asNullableString = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (value == null) return null;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === "number" && !Number.isNaN(value) ? value : fallback;

const asNullableNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
};

const parseProcessResult = (value: unknown): OrderProcessResult | null => {
  const raw =
    typeof value === "string" ? value.toUpperCase() : "";
  if (
    raw === "SUCCESS" ||
    raw === "FAILED" ||
    raw === "IN_PROGRESS" ||
    raw === "CANCELED" ||
    raw === "CANCELLED" ||
    raw === "UNKNOWN"
  ) {
    return raw === "CANCELLED" ? "CANCELED" : (raw as OrderProcessResult);
  }
  return null;
};

/**
 * Orders stuck as pending-create-order after SIESA already created them
 * (reprocess noise). Block reprocess only; keep API status/error for display.
 *
 * ASSUMPTION: matching is UI-only; API still returns the original status.
 */
const ALREADY_CREATED_IN_SIESA_MARKER = "la orden ya fue creada en siesa";

export const isAlreadyCreatedInSiesaOrder = (
  order: Pick<OrderListItem, "status" | "errorDetail">
): boolean => {
  const status = (order.status ?? "").toLowerCase();
  if (status !== "pending-create-order") return false;
  const detail = (order.errorDetail ?? "").toLowerCase();
  return detail.includes(ALREADY_CREATED_IN_SIESA_MARKER);
};

/**
 * Keeps API values intact for display and filtering.
 * Reprocess eligibility is evaluated separately via `canReprocessOrder`.
 */
export const normalizeOrderListItemForDisplay = (
  order: OrderListItem
): OrderListItem => order;

/**
 * Eligible when `sale.status_sale` is in the reprocessable set (§3.3).
 */
export const canReprocessOrder = (order: OrderListItem): boolean => {
  if (isAlreadyCreatedInSiesaOrder(order)) return false;
  const status = (order.status ?? "").toLowerCase();
  if (!status || status === "—") return false;
  if (NON_REPROCESSABLE_STATUSES.has(status)) return false;
  return REPROCESSABLE_STATUSES.has(status);
};

/** Human-readable reason when an order cannot be reprocessed (checkbox tooltip). */
export const getReprocessIneligibilityReason = (
  order: OrderListItem
): string => {
  if (isAlreadyCreatedInSiesaOrder(order)) {
    return "Esta orden ya fue creada en SIESA y no puede reprocesarse.";
  }

  const status = (order.status ?? "").toLowerCase();
  if (!status || status === "—") {
    return "Estado de la orden desconocido; no es elegible para reproceso.";
  }

  if (NON_REPROCESSABLE_STATUSES.has(status)) {
    return `Las órdenes en estado "${getOrderStatusLabel(status)}" no pueden reprocesarse.`;
  }

  if (!REPROCESSABLE_STATUSES.has(status)) {
    return `El estado "${getOrderStatusLabel(status)}" no está habilitado para reproceso.`;
  }

  return "Esta orden no puede reprocesarse.";
};

export const mapOrderListItem = (raw: unknown): OrderListItem => {
  if (!isRecord(raw)) {
    throw new Error("Formato de orden inválido.");
  }

  const mapped: OrderListItem = {
    orderVTEXId: asString(raw.orderVTEXId),
    orderSIESAId: asNullableString(raw.orderSIESAId),
    documentId: asString(raw.documentId),
    names: asString(raw.names),
    creationDate: asNullableString(raw.creationDate),
    trackingNumber: asNullableString(raw.trackingNumber),
    status: asString(raw.status, "—"),
    total: asNumber(raw.total),
    processedAt: asNullableString(raw.processedAt),
    errorDetail: asNullableString(raw.errorDetail),
    retryCount: asNullableNumber(raw.retryCount),
    attemptCount: asNullableNumber(raw.attemptCount),
  };

  return normalizeOrderListItemForDisplay(mapped);
};

export const mapOrderListResponse = (data: unknown): OrderListResponse => {
  if (!isRecord(data) || !Array.isArray(data.content)) {
    throw new Error(
      "Formato de respuesta inesperado. Se esperaba un listado de órdenes."
    );
  }

  const content = data.content.map(mapOrderListItem);
  const totalElements = asNumber(data.totalElements, content.length);
  const pageSize = asNumber(data.pageSize ?? data.size, content.length || 10);
  const currentPage = asNumber(data.currentPage ?? data.number, 1);
  const totalPages =
    asNumber(data.totalPages) ||
    Math.ceil(totalElements / (pageSize || 1)) ||
    0;

  return {
    content,
    totalElements,
    totalPages,
    currentPage: Math.max(currentPage, 1),
    pageSize,
  };
};

/**
 * `totalElements` from the backend ignores the status/search filters: it is
 * computed from a fixed status subset (`created-order, delivered, invoiced,
 * handling`) inside the date window, while `content[]` does honor every
 * filter. Verified 2026-09-23: `status=pending-create-order` reported 21,709.
 * It matches "Creado" only because that subset is almost the Creado group.
 *
 * Use this only for the unfiltered/date-only view; narrowed lists use the
 * exact background count (`useGetOrders().filteredCount`). It floors the API
 * total at what is already on screen, so the pager never reports fewer rows
 * than are visible. Note: `pageable.offset` is the index of the page's first
 * row (pageNumber × pageSize), not a total.
 */
export const resolveOrderListTotalElements = (
  apiTotal: number,
  contentLength: number,
  page: number,
  pageSize: number
): number => {
  const safePage = Math.max(page, 1);
  const safeSize = Math.max(pageSize, 1);
  const knownRows = (safePage - 1) * safeSize + contentLength;
  return Math.max(apiTotal, knownRows);
};

/** Build detail from a list row when no detail endpoint exists. */
export const orderListItemToDetail = (order: OrderListItem): OrderDetail => ({
  ...order,
});

export const mapOrderReprocessItemResult = (
  raw: unknown
): OrderReprocessItemResult => {
  if (!isRecord(raw)) {
    return {
      orderId: "",
      success: false,
      skipped: false,
      message: "Respuesta de reproceso inválida.",
      statusBefore: null,
      statusAfter: null,
      processResult: null,
      errorDetail: null,
    };
  }

  return {
    orderId: asString(raw.orderId),
    success: Boolean(raw.success),
    skipped: Boolean(raw.skipped),
    message: asString(raw.message),
    statusBefore: asNullableString(raw.statusBefore),
    statusAfter: asNullableString(raw.statusAfter),
    processResult: parseProcessResult(raw.processResult),
    errorDetail: asNullableString(raw.errorDetail),
  };
};

/** Parsed SIESA / audit errorDetail for UI (reprocess modal). */
export interface ParsedReprocessErrorDetail {
  /** Short human message (e.g. "Error al importar el plano"). */
  summary: string;
  /** Optional line-level details from SIESA `detalle[]`. */
  lines: string[];
  raw: string;
}

/**
 * Formats `errorDetail` from reprocess results.
 * ASSUMPTION: Often `Error: {json}` with `mensaje` + `detalle[].f_detalle`.
 * Plain text / already-analyzed Spanish strings are shown as-is.
 */
export const formatReprocessErrorDetail = (
  errorDetail: string | null | undefined,
  fallbackMessage?: string | null
): ParsedReprocessErrorDetail => {
  const fallback = (fallbackMessage ?? "").trim() || "Sin detalle del error.";
  const raw = (errorDetail ?? "").trim();
  if (!raw) {
    return { summary: fallback, lines: [], raw: "" };
  }

  const withoutPrefix = raw.replace(/^Error:\s*/i, "").trim();

  try {
    const parsed: unknown = JSON.parse(withoutPrefix);
    if (isRecord(parsed)) {
      const mensaje = asString(parsed.mensaje ?? parsed.message);
      const detalleRaw = parsed.detalle;
      const lines: string[] = [];

      if (Array.isArray(detalleRaw)) {
        for (const row of detalleRaw) {
          if (!isRecord(row)) continue;
          const fDetalle = asString(
            row.f_detalle ?? row.fDetalle ?? row.detalle
          );
          if (!fDetalle) continue;
          const linea = asString(row.f_nro_linea ?? row.fNroLinea);
          lines.push(linea ? `Línea ${linea}: ${fDetalle}` : fDetalle);
        }
      }

      return {
        summary: mensaje || withoutPrefix || fallback,
        lines,
        raw,
      };
    }
  } catch {
    // Not nested JSON — keep readable text.
  }

  return {
    summary: withoutPrefix || raw,
    lines: [],
    raw,
  };
};

/**
 * Maps POST /order/reprocess body to one result per requested id.
 */
export const parseReprocessResults = (
  data: unknown,
  requestedIds: string[]
): OrderReprocessItemResult[] => {
  const mapped: OrderReprocessItemResult[] = [];

  if (isRecord(data) && Array.isArray(data.results)) {
    for (const row of data.results) {
      mapped.push(mapOrderReprocessItemResult(row));
    }
  } else if (isRecord(data) && data.orderId != null) {
    mapped.push(mapOrderReprocessItemResult(data));
  }

  const byId = new Map(
    mapped.filter((r) => r.orderId).map((r) => [r.orderId, r])
  );

  return requestedIds.map((id) => {
    const found = byId.get(id);
    if (found) return found;
    if (mapped.length === 1 && requestedIds.length === 1) {
      return { ...mapped[0], orderId: id };
    }
    return {
      orderId: id,
      success: false,
      skipped: false,
      message: "El servicio no devolvió resultado para la orden.",
      statusBefore: null,
      statusAfter: null,
      processResult: null,
      errorDetail: null,
    };
  });
};

/** @deprecated Prefer parseReprocessResults for batch responses */
export const parseReprocessResponse = (
  data: unknown,
  fallbackOrderId: string
): OrderReprocessItemResult =>
  parseReprocessResults(data, [fallbackOrderId])[0];

export const ORDER_BACKEND_UNAVAILABLE_MESSAGE =
  "El servicio de órdenes no está disponible en este momento. Inténtalo más tarde o contacta a backend.";

const ORDER_MESSAGE_TRANSLATIONS: { match: RegExp; es: string }[] = [
  {
    match:
      /order backend is currently unavailable|backend is currently unavailable|service unavailable|502|503/i,
    es: ORDER_BACKEND_UNAVAILABLE_MESSAGE,
  },
  {
    match: /failed to fetch|network ?error|load failed|connection refused/i,
    es: "No se pudo conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.",
  },
  {
    match:
      /unauthorized|forbidden|not authenticated|invalid[_ ]token|jwt expired|401|403/i,
    es: "Tu sesión no es válida o expiró. Por favor, inicia sesión nuevamente.",
  },
  {
    match: /timeout|timed out|gateway timeout|504|aborted/i,
    es: "La operación tardó demasiado. Por favor, inténtelo de nuevo.",
  },
  {
    match: /not found|404/i,
    es: ORDER_REPROCESS_UNAVAILABLE_MESSAGE,
  },
  {
    match: /method not allowed|405/i,
    es: ORDER_REPROCESS_METHOD_NOT_ALLOWED_MESSAGE,
  },
  {
    match: /already (running|in progress)/i,
    es: "Ya hay un reproceso en curso. Espera a que finalice antes de iniciar otro.",
  },
  {
    match: /internal server error|unexpected error|\b500\b/i,
    es: "Ocurrió un error en el servidor. Por favor, inténtelo de nuevo en unos minutos.",
  },
  {
    // §3.2: dates outside last-2-months window → 400
    match:
      /2 months|últimos 2 meses|outside.*(window|range)|fuera de la ventana|date.*(window|range)|fromDate|toDate/i,
    es: ORDER_LIST_DATE_WINDOW_MESSAGE,
  },
  {
    match: /more than 20|máximo 20|empty body|blank id|ids? in blank/i,
    es: "La solicitud de reproceso no es válida (máximo 20 órdenes por lote).",
  },
  {
    match: /bad request|\b400\b/i,
    es: "La solicitud no es válida. Revisa filtros y fechas (últimos 2 meses).",
  },
];

export const translateOrderMessage = (
  raw: string,
  fallback: string = ORDER_LOAD_ERROR_MESSAGE
): string => {
  const text = (raw ?? "").trim();
  if (!text) return fallback;
  if (/[áéíóúñ¿¡]/i.test(text)) return text;

  for (const { match, es } of ORDER_MESSAGE_TRANSLATIONS) {
    if (match.test(text)) return es;
  }

  return fallback;
};

/**
 * Prefer response body (JSON or plain text). Gateway may return 401/503 with
 * "Order Backend is currently unavailable" — that is NOT a session error.
 */
export const resolveOrderLoadError = async (
  response: Response
): Promise<string> => {
  const statusHint = `${response.status} ${response.statusText}`;
  let bodyText = "";

  try {
    bodyText = (await response.text()).trim();
  } catch {
    bodyText = "";
  }

  if (bodyText) {
    try {
      const parsed: unknown = JSON.parse(bodyText);
      if (isRecord(parsed)) {
        const message = asString(
          parsed.message ?? parsed.error ?? parsed.detail ?? parsed.error_description
        );
        if (message) {
          return translateOrderMessage(`${message} ${statusHint}`, message);
        }
      }
    } catch {
      // plain-text body from gateway
    }

    return translateOrderMessage(`${bodyText} ${statusHint}`, bodyText);
  }

  return translateOrderMessage(statusHint, ORDER_LOAD_ERROR_MESSAGE);
};
